import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";
import { useSpring, animated } from "react-spring";
import { Text } from "./Text";
import { Flex } from "./Flex";
import { FaThumbsDown, FaThumbsUp, FaTimes } from "react-icons/fa";
import { mix } from "polished";
import { uniqBy } from "lodash";
import ReactMarkdown from "react-markdown";
import { format } from "date-fns";
import { space, SpaceProps } from "styled-system";
import { useFeatures } from "../hooks/FeaturesProvider";
import {
  NewFeature,
  NewFeatureReaction,
  NewFeatureReactionSpec,
} from "@aika/types";

const RateButton = styled.button`
  position: relative;
  display: flex;
  padding: 3px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 0;
  background: none;
  margin-left: 6px;
  color: ${(props) => mix(0.3, "#888", props.theme.colors[props.color])};

  &:hover {
    color: ${(props) => props.theme.colors[props.color]};
  }

  &:active,
  &:focus {
    outline: 0;
  }
`;

const RawContent = styled.div<SpaceProps>`
  p {
    margin-bottom: 6px;
  }
  ${space};
`;

const Container = styled(animated.div)`
  background: ${(props) => props.theme.colors.background};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  z-index: 999999;
  overflow-y: auto;
  box-shadow: -3px 0 3px rgba(0, 0, 0, 0.15);
`;

const CloseButton = styled.button`
  display: flex;
  padding: 3px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 0;
  background: none;
  color: ${(props) => mix(0.9, "#888", props.theme.colors.body)};

  &:hover {
    color: ${(props) => props.theme.colors.body};
  }
`;

const FeatureVideo = styled.video`
  max-width: 100%;
  margin-bottom: 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
`;

type NewFeaturesDisplayProps = {
  items: NewFeature[];
  reactions: NewFeatureReaction[];
  onVote: (id: string, upVote: boolean) => void;
  onClose: () => void;
};

const NewFeaturesDisplay = ({
  items,
  reactions,
  onVote,
  onClose,
}: NewFeaturesDisplayProps) => {
  const props = useSpring({
    transform: "translate3d(0px, 0px, 0px)",
    from: {
      transform: "translate3d(300px, 0px, 0px)",
    },
  });

  const upVote = (id: string) => {
    onVote(id, true);
  };

  const downVote = (id: string) => {
    onVote(id, false);
  };

  const isUpvote = (id: string) =>
    reactions.find((x) => x.featureId === id)?.upvote === true;
  const isDownvote = (id: string) =>
    reactions.find((x) => x.featureId === id)?.upvote === false;

  return (
    <Container style={props}>
      <Flex
        justifyContent="space-between"
        p={3}
        pt={2}
        pb={2}
        style={{ borderBottom: "1px solid rgba(128,128,128, 0.2)" }}
      >
        <Text fontSize={5} fontWeight={600}>
          What's New?
        </Text>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
      </Flex>
      {items.map((x) => (
        <Flex flexDirection="column" p={3} pt={3}>
          <Text fontSize={2} style={{ opacity: 0.5 }}>
            {format(new Date(x.dateAdded), "do MMMM yyyy")}
          </Text>
          <Text fontSize={4} fontWeight={600} mb={2}>
            {x.name}
          </Text>
          {x.videoUrl && <FeatureVideo src={x.videoUrl} autoPlay loop />}
          <RawContent mb={2}>
            <ReactMarkdown>{x.content}</ReactMarkdown>
          </RawContent>
          <Flex alignItems="center" justifyContent="space-between">
            <a href={x.learnMoreUrl} target="_blank" rel="noreferrer">
              Learn More
            </a>
            <Flex alignItems="center">
              <RateButton
                title="I like it!"
                onClick={() => upVote(x.id)}
                style={{ top: -1 }}
                color={isUpvote(x.id) ? "positive" : "body"}
              >
                <FaThumbsUp />
              </RateButton>
              <RateButton
                title="This isn't useful."
                onClick={() => downVote(x.id)}
                style={{ top: 1 }}
                color={isDownvote(x.id) ? "danger" : "body"}
              >
                <FaThumbsDown />
              </RateButton>
            </Flex>
          </Flex>
        </Flex>
      ))}
    </Container>
  );
};

export const NewFeatures = ({
  shown,
  onClose,
}: {
  shown: boolean;
  onClose: () => void;
}) => {
  const [reactions, setReactions] = useState<NewFeatureReaction[]>([]);
  const items = useFeatures();

  useEffect(() => {
    axios.get("/api/feature-reactions").then(({ data }) => setReactions(data));
  }, []);

  if (!shown) {
    return null;
  }

  const onVote = (id: string, isUpvote: boolean) => {
    const spec: NewFeatureReactionSpec = {
      featureId: id,
      upvote: isUpvote,
    };
    axios.post("/api/feature-reactions", spec).then(({ data }) => {
      setReactions((old) => uniqBy([data, ...old], "featureId"));
    });
  };

  return (
    <NewFeaturesDisplay
      items={items}
      reactions={reactions}
      onVote={onVote}
      onClose={onClose}
    />
  );
};
